import {
    Box,
    Chip,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Tabs,
    Tab,
} from '@material-ui/core';
import { Content, Header, Page, Progress } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

type PackageManager = 'all' | 'npm' | 'python' | 'nuget';

const useStyles = makeStyles(theme => ({
    searchBox: {
        marginBottom: theme.spacing(3),
        width: '100%',
        maxWidth: 600,
    },
    tableContainer: {
        marginTop: theme.spacing(2),
    },
    packageName: {
        fontFamily: 'monospace',
        fontSize: '0.875rem',
        fontWeight: 500,
    },
    version: {
        fontFamily: 'monospace',
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
    componentChip: {
        margin: theme.spacing(0.5),
        fontSize: '0.75rem',
    },
    tableHeader: {
        fontWeight: 600,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    statsBox: {
        display: 'flex',
        gap: theme.spacing(3),
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.default,
        borderRadius: theme.shape.borderRadius,
    },
    statItem: {
        display: 'flex',
        flexDirection: 'column',
    },
    statValue: {
        fontSize: '2rem',
        fontWeight: 600,
        color: theme.palette.primary.main,
    },
    statLabel: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        textTransform: 'uppercase',
    },
    managerBadge: {
        fontFamily: 'monospace',
        fontSize: '0.65rem',
        padding: '2px 6px',
        borderRadius: 3,
        fontWeight: 600,
        textTransform: 'uppercase',
        display: 'inline-block',
        marginRight: theme.spacing(1),
    },
    npmBadge: {
        backgroundColor: '#CC3534',
        color: '#fff',
    },
    pythonBadge: {
        backgroundColor: '#3776AB',
        color: '#fff',
    },
    nugetBadge: {
        backgroundColor: '#004880',
        color: '#fff',
    },
    tabs: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        marginBottom: theme.spacing(2),
    },
}));

interface DependencyInfo {
    name: string;
    manager: PackageManager;
    versions: Set<string>;
    components: Array<{ name: string; entityRef: string; version: string }>;
}

export const DependencyExplorerPage = () => {
    const classes = useStyles();
    const catalogApi = useApi(catalogApiRef);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeManager, setActiveManager] = useState<PackageManager>('all');

    const { value: dependencies, loading, error } = useAsync(async () => {
        const entities = await catalogApi.getEntities({
            filter: { kind: 'Component' },
        });

        const depMap = new Map<string, DependencyInfo>();

        entities.items.forEach(entity => {
            const annotations = entity.metadata.annotations || {};

            // Process unified dependencies first (new format)
            const unifiedDepsJson = annotations['dependencies/all'];
            if (unifiedDepsJson) {
                try {
                    const deps = JSON.parse(unifiedDepsJson);
                    deps.forEach((dep: any) => {
                        const key = `${dep.name}::${dep.manager}`;
                        if (!depMap.has(key)) {
                            depMap.set(key, {
                                name: dep.name,
                                manager: dep.manager,
                                versions: new Set(),
                                components: [],
                            });
                        }
                        const info = depMap.get(key)!;
                        info.versions.add(dep.version);
                        info.components.push({
                            name: entity.metadata.name,
                            entityRef: `component:default/${entity.metadata.name}`,
                            version: dep.version,
                        });
                    });
                } catch (e) {
                    // Skip invalid JSON
                }
            } else {
                // Fallback to legacy npm-only format
                const depsJson = annotations['npm/dependencies'];
                const devDepsJson = annotations['npm/devDependencies'];

                const processDeps = (json: string | undefined) => {
                    if (!json) return;
                    try {
                        const deps = JSON.parse(json);
                        Object.entries(deps).forEach(([name, version]) => {
                            const key = `${name}::npm`;
                            if (!depMap.has(key)) {
                                depMap.set(key, {
                                    name,
                                    manager: 'npm',
                                    versions: new Set(),
                                    components: [],
                                });
                            }
                            const info = depMap.get(key)!;
                            info.versions.add(version as string);
                            info.components.push({
                                name: entity.metadata.name,
                                entityRef: `component:default/${entity.metadata.name}`,
                                version: version as string,
                            });
                        });
                    } catch (e) {
                        // Skip invalid JSON
                    }
                };

                processDeps(depsJson);
                processDeps(devDepsJson);
            }
        });

        return Array.from(depMap.values()).sort((a, b) =>
            b.components.length - a.components.length || a.name.localeCompare(b.name)
        );
    }, [catalogApi]);

    const filteredDependencies = useMemo(() => {
        if (!dependencies) return [];

        let filtered = dependencies;

        // Filter by manager
        if (activeManager !== 'all') {
            filtered = filtered.filter(dep => dep.manager === activeManager);
        }

        // Filter by search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(dep =>
                dep.name.toLowerCase().includes(term) ||
                dep.components.some(c => c.name.toLowerCase().includes(term))
            );
        }

        return filtered;
    }, [dependencies, searchTerm, activeManager]);

    const stats = useMemo(() => {
        if (!dependencies) return {
            totalDeps: 0,
            totalComponents: 0,
            avgDepsPerComponent: 0,
            byManager: { npm: 0, python: 0, nuget: 0 }
        };

        const componentSet = new Set<string>();
        const managerCounts = { npm: 0, python: 0, nuget: 0 };

        dependencies.forEach(dep => {
            dep.components.forEach(c => componentSet.add(c.entityRef));
            if (dep.manager !== 'all') {
                managerCounts[dep.manager]++;
            }
        });

        return {
            totalDeps: dependencies.length,
            totalComponents: componentSet.size,
            avgDepsPerComponent: componentSet.size > 0
                ? Math.round(dependencies.reduce((sum, d) => sum + d.components.length, 0) / componentSet.size)
                : 0,
            byManager: managerCounts,
        };
    }, [dependencies]);

    const getManagerBadgeClass = (manager: string) => {
        switch (manager) {
            case 'npm': return classes.npmBadge;
            case 'python': return classes.pythonBadge;
            case 'nuget': return classes.nugetBadge;
            default: return '';
        }
    };

    if (loading) {
        return (
            <Page themeId="tool">
                <Header title="Dependency Explorer" subtitle="Explore NPM dependencies across all components" />
                <Content>
                    <Progress />
                </Content>
            </Page>
        );
    }

    if (error) {
        return (
            <Page themeId="tool">
                <Header title="Dependency Explorer" subtitle="Explore NPM dependencies across all components" />
                <Content>
                    <Typography color="error">Failed to load dependencies: {error.message}</Typography>
                </Content>
            </Page>
        );
    }

    return (
        <Page themeId="tool">
            <Header title="Dependency Explorer" subtitle="Explore package dependencies across all components" />
            <Content>
                <Box className={classes.statsBox}>
                    <Box className={classes.statItem}>
                        <Typography className={classes.statValue}>{stats.totalDeps}</Typography>
                        <Typography className={classes.statLabel}>Total Dependencies</Typography>
                    </Box>
                    <Box className={classes.statItem}>
                        <Typography className={classes.statValue}>{stats.totalComponents}</Typography>
                        <Typography className={classes.statLabel}>Components</Typography>
                    </Box>
                    <Box className={classes.statItem}>
                        <Typography className={classes.statValue}>{stats.avgDepsPerComponent}</Typography>
                        <Typography className={classes.statLabel}>Avg Deps/Component</Typography>
                    </Box>
                </Box>

                <Tabs
                    value={activeManager}
                    onChange={(_, newValue) => setActiveManager(newValue)}
                    className={classes.tabs}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab label={`All (${stats.totalDeps})`} value="all" />
                    {stats.byManager.npm > 0 && (
                        <Tab label={`NPM (${stats.byManager.npm})`} value="npm" />
                    )}
                    {stats.byManager.python > 0 && (
                        <Tab label={`Python (${stats.byManager.python})`} value="python" />
                    )}
                    {stats.byManager.nuget > 0 && (
                        <Tab label={`NuGet (${stats.byManager.nuget})`} value="nuget" />
                    )}
                </Tabs>

                <TextField
                    className={classes.searchBox}
                    placeholder="Search dependencies or components..."
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    fullWidth
                />

                <Paper>
                    <TableContainer className={classes.tableContainer}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell className={classes.tableHeader}>Dependency</TableCell>
                                    <TableCell className={classes.tableHeader} align="center">Manager</TableCell>
                                    <TableCell className={classes.tableHeader} align="center">Used By</TableCell>
                                    <TableCell className={classes.tableHeader}>Versions</TableCell>
                                    <TableCell className={classes.tableHeader}>Components</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredDependencies.map((dep) => (
                                    <TableRow key={`${dep.name}-${dep.manager}`}>
                                        <TableCell>
                                            <span className={classes.packageName}>{dep.name}</span>
                                        </TableCell>
                                        <TableCell align="center">
                                            <span className={`${classes.managerBadge} ${getManagerBadgeClass(dep.manager)}`}>
                                                {dep.manager}
                                            </span>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={dep.components.length}
                                                size="small"
                                                color="primary"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {Array.from(dep.versions).map(v => (
                                                <Box key={v}>
                                                    <span className={classes.version}>{v}</span>
                                                </Box>
                                            ))}
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                {dep.components.map((comp, idx) => (
                                                    <Chip
                                                        key={`${comp.entityRef}-${idx}`}
                                                        label={comp.name}
                                                        size="small"
                                                        className={classes.componentChip}
                                                        component={Link}
                                                        to={`/catalog/default/component/${comp.name}`}
                                                        clickable
                                                    />
                                                ))}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {filteredDependencies.length === 0 && (
                    <Box textAlign="center" mt={4}>
                        <Typography variant="h6" color="textSecondary">
                            No dependencies found
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Try adjusting your search term or filter
                        </Typography>
                    </Box>
                )}
            </Content>
        </Page>
    );
};
